"use client";

import Image from "@/components/OptimizedImage";
import Link from "next/link";
import * as LucideIcons from "lucide-react";
import { ChevronRight } from "lucide-react";

interface FeatureLink {
  text: string;
  href: string;
}

interface FeatureItem {
  iconName?: string;
  label?: string; 
  title: string;
  description: string;
  image: string;
  link: FeatureLink;
}

const Features = ({ data }: { data: FeatureItem[] }) => {
  if (!data || data.length === 0) return null;

  return (
    <section className="section-padding container">
      <div className="flex flex-col gap-24">
        {data.map((feature, index) => {
          const IconComponent = feature.iconName 
            ? (LucideIcons as any)[feature.iconName] 
            : null;

          return (
            <div
              key={index}
              className={`flex flex-col items-center justify-between gap-8 md:flex-row md:gap-16 ${
                index % 2 !== 0 ? "md:flex-row-reverse" : ""
              }`}
            >
              {/* Image Section */}
              <Link 
                href={feature.link.href} 
                className="relative h-[340px] w-full sm:h-[600px] lg:w-[50%] lg:shrink-0 rounded-lg overflow-hidden group"
              >
                <div className="relative h-full w-full">
                  <Image
                    src={feature.image}
                    alt={feature.title}
                    fill
                    unoptimized
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              </Link>
              
              {/* Content Section */}
              <div className="flex-1">
                <div className="space-y-6 md:space-y-8 lg:space-y-10">
                  {(IconComponent || feature.label) && (
                    <div className="flex items-center gap-3">
                      {IconComponent && <IconComponent className="size-6" />}
                      {feature.label && (
                        <p className="text-xl font-medium leading-8">
                          {feature.label}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <h3 className="text-2xl font-medium md:text-3xl lg:text-4xl leading-tight">
                    {feature.title}
                  </h3>
                  <p className="text-xl leading-8 text-muted-foreground">
                    {feature.description}
                  </p>
                </div>

                <Link 
                  href={feature.link.href}
                  className="inline-flex items-center gap-2 mt-8 px-6 py-2 rounded-lg text-sm font-bold border-2 border-black bg-transparent text-black hover:bg-black hover:text-white transition-colors"
                >
                  {feature.link.text}
                  <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Features;